import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Toaster } from "./components/ui/sonner";

// Pages — Public Marketing
import HomeQualifier from "./pages/HomeQualifier";
import ProfessionalsPage from "./pages/ProfessionalsPage";
import ParticularesPage from "./pages/ParticularesPage";
import ParticulierChoice from "./pages/ParticulierChoice";
import ParticulierFrance from "./pages/ParticulierFrance";
import NotEligiblePage from "./pages/NotEligiblePage";

// Pages — App
import ProfileChoice from "./pages/ProfileChoice";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import DashboardPage from "./pages/DashboardPage";
import CheckoutPage from "./pages/CheckoutPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import PaymentFailurePage from "./pages/PaymentFailurePage";
import OrderStatusPage from "./pages/OrderStatusPage";
import PropertiesPage from "./pages/PropertiesPage";
import PropertyFormPage from "./pages/PropertyFormPage";
import AdminDashboard from "./pages/AdminDashboard";
import ClaimsPage from "./pages/ClaimsPage";
import AffiliateDashboard from "./pages/AffiliateDashboard";
import CGV from "./pages/CGV";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import NotFoundPage from "./pages/NotFoundPage";
import CookieConsent from "./components/CookieConsent";
import GoogleAnalytics from "./components/GoogleAnalytics";
import ErrorBoundary from "./components/ErrorBoundary";
import MobileCallButton from "./components/MobileCallButton";
import { useAffiliateRef } from "./hooks/useAffiliateRef";

import "@/App.css";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#16A34A]"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/connexion" replace />;
  }
  
  return children;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#16A34A]"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/connexion" replace />;
  }
  
  if (user.role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Public Route (redirect to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#16A34A]"></div>
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function App() {
  // Capture ?ref=AFFILIATE_CODE from any landing URL → localStorage (30j TTL)
  useAffiliateRef();
  return (
    <HelmetProvider>
    <ErrorBoundary>
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public Marketing Routes */}
          <Route path="/" element={<HomeQualifier />} />
          <Route path="/professionnel" element={<Navigate to="/fr/professionnels" replace />} />
          <Route path="/fr/professionnels" element={<ProfessionalsPage locale="fr" />} />
          <Route path="/es/profesionales" element={<ProfessionalsPage locale="es" />} />
          <Route path="/en/professionals" element={<ProfessionalsPage locale="en" />} />
          <Route path="/particulier" element={<ParticulierChoice />} />
          <Route path="/particulier-espagne" element={<ParticularesPage />} />
          {/* Offre particulier France desactivee — redirige vers la page de choix */}
          <Route path="/particulier-france" element={<Navigate to="/particulier" replace />} />
          <Route path="/es/particulares" element={<Navigate to="/particulier-espagne" replace />} />
          <Route path="/not-eligible" element={<NotEligiblePage />} />
          <Route path="/conditions-generales-de-vente" element={<CGV />} />
          <Route path="/politique-de-confidentialite" element={<PrivacyPolicy />} />

          {/* App Routes */}
          <Route path="/profil" element={<ProfileChoice />} />
          <Route 
            path="/connexion" 
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            } 
          />
          <Route 
            path="/inscription" 
            element={
              <PublicRoute>
                <RegisterPage />
              </PublicRoute>
            } 
          />
          <Route path="/mot-de-passe-oublie" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          
          {/* Protected Routes */}
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/checkout" 
            element={
              <ProtectedRoute>
                <CheckoutPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/paiement-succes" 
            element={
              <ProtectedRoute>
                <PaymentSuccessPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/paiement-echec" 
            element={
              <ProtectedRoute>
                <PaymentFailurePage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/commande/:orderId" 
            element={
              <ProtectedRoute>
                <OrderStatusPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/logements" 
            element={
              <ProtectedRoute>
                <PropertiesPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/logements/nouveau/:orderId" 
            element={
              <ProtectedRoute>
                <PropertyFormPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/logements/:propertyId/editer" 
            element={
              <ProtectedRoute>
                <PropertyFormPage />
              </ProtectedRoute>
            } 
          />
          
          {/* Affiliate Route */}
          <Route 
            path="/partenaire" 
            element={
              <ProtectedRoute>
                <AffiliateDashboard />
              </ProtectedRoute>
            } 
          />

          {/* Claims Route */}
          <Route 
            path="/logements/:propertyId/sinistres" 
            element={
              <ProtectedRoute>
                <ClaimsPage />
              </ProtectedRoute>
            } 
          />

          {/* Admin Route */}
          <Route 
            path="/admin" 
            element={
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            } 
          />
          
          {/* 404 — must be the last route */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        <MobileCallButton />
        <CookieConsent />
        <GoogleAnalytics />
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
    </ErrorBoundary>
    </HelmetProvider>
  );
}

export default App;
